import React from "react";
import { useQuery } from "@tanstack/react-query";
import { getAllCards } from "../../../services/patient/healthCardService";
import Loader from "../../../components/loader/loader";
import { CreditCard, Zap, ShieldCheck, Crown, Info } from "lucide-react";

const MyHealthCard = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["all-cards"],
    queryFn: async () => {
      const res = await getAllCards();
      return res?.data?.data;
    },
    retry: false,
  });

  // --- LIGHT THEME HELPER ---
  const getTheme = (name = "") => {
    if (name.includes("Silver")) return {
      grad: "from-slate-400 via-slate-500 to-slate-600",
      bg: "bg-slate-50",
      accent: "text-slate-600",
      button: "from-slate-500 to-slate-700",
      icon: <ShieldCheck size={28} />
    };
    if (name.includes("Gold")) return {
      grad: "from-amber-400 via-orange-500 to-yellow-600",
      bg: "bg-orange-50",
      accent: "text-orange-600",
      button: "from-orange-400 to-amber-600",
      icon: <Zap size={28} />
    };
    if (name.includes("Platinum")) return {
      grad: "from-indigo-500 via-purple-600 to-pink-600",
      bg: "bg-purple-50",
      accent: "text-purple-600",
      button: "from-indigo-600 to-purple-600",
      icon: <Crown size={28} />
    };
    // Default / Empty
    return {
      grad: "from-gray-200 via-gray-300 to-gray-400",
      bg: "bg-gray-50",
      accent: "text-gray-400",
      button: "from-gray-700 to-gray-900",
      icon: <CreditCard size={28} />
    };
  };

  const activeCardName = ""; // Set to a card name to test
  const activeTheme = getTheme(activeCardName);

  return (
    <div className="min-h-screen bg-[#f8fafc] px-4 sm:px-8 font-sans text-slate-900">
      {isLoading && <Loader />}

      <div className="max-w-7xl mx-auto">
        {/* ================= MY CARD SECTION ================= */}
        <section className="mb-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-2 gap-4">
            <div>
              <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">My Health Cards</h1>
              <p className="text-slate-500 mt-1">Manage your active digital health credentials.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* The Visual Card */}
            <div className="lg:col-span-5 relative group">
              {/* Soft outer glow */}
              <div className={`absolute -inset-1 bg-gradient-to-r ${activeTheme.grad} rounded-[2rem] blur-xl opacity-20 group-hover:opacity-30 transition duration-700`}></div>
              
              <div className={`relative aspect-[1.986/1] w-full bg-white rounded-3xl border border-slate-200 p-6 shadow-[0_20px_50px_rgba(0,0,0,0.05)] flex flex-col justify-between overflow-hidden`}>
                {/* Visual Depth Decoration */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-slate-100 to-transparent opacity-50 rounded-bl-full"></div>

                <div className="flex justify-between items-start relative z-10">
                  <div className="space-y-1">
                    <span className="text-[10px] font-black tracking-[0.3em] uppercase text-blue-600">Health Panel</span>
                    <div className="h-10 w-14 bg-gradient-to-br from-yellow-100 via-yellow-300 to-yellow-500 rounded-lg shadow-sm flex items-center justify-center border border-yellow-400/20">
                       <div className="grid grid-cols-3 gap-1 w-full h-full p-2.5 opacity-30">
                          {[...Array(6)].map((_,i) => <div key={i} className="border-r border-b border-black/40"></div>)}
                       </div>
                    </div>
                  </div>
                  <div className="text-slate-200">{activeTheme.icon}</div>
                </div>

                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold mb-1">Status</p>
                  <h3 className={`text-2xl font-black italic tracking-tight uppercase ${activeCardName ? 'bg-clip-text text-transparent bg-gradient-to-r ' + activeTheme.grad : 'text-slate-300'}`}>
                    {activeCardName || "No Active Card"}
                  </h3>
                </div>

                <div className="flex justify-between items-end relative z-10 pt-4 border-t border-slate-50">
                  <div>
                    <p className="text-[9px] uppercase tracking-widest text-slate-400">Card Holder</p>
                    <p className="font-bold text-slate-800">GUEST PATIENT</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[9px] uppercase tracking-widest text-slate-400">Expiry</p>
                    <p className="font-bold text-slate-800">-- / --</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Message */}
            <div className="lg:col-span-7">
              {!activeCardName ? (
                <div className="p-8 rounded-3xl bg-white border border-slate-200 shadow-sm relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-4 opacity-5 text-blue-600"><CreditCard size={80} /></div>
                  <div className="flex gap-5 relative z-10">
                    <div className="p-4 bg-blue-50 rounded-2xl text-blue-600 h-fit"><Info size={28} /></div>
                    <div>
                      <h4 className="text-xl font-bold text-slate-900 mb-2">Enhance your experience</h4>
                      <p className="text-slate-600 leading-relaxed">
                        Secure your healthcare benefits today. Having a health card ensures <strong>cashless visits</strong>, lab discounts, and priority specialist access.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-8 rounded-3xl bg-emerald-50 border border-emerald-100">
                   <h4 className="text-xl font-bold text-emerald-700 mb-2">Membership Active</h4>
                   <p className="text-emerald-600/80">Your profile is currently linked to an active health plan. All discounts will be automatically applied at checkout.</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ================= ALL CARDS SECTION ================= */}
        <section>
          <div className="mb-4">
            <h2 className="text-2xl font-black text-slate-900 flex items-center gap-3">
              Available Memberships
              <span className="h-1.5 w-1.5 rounded-full bg-blue-600 mt-1"></span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {data?.map((card) => {
              const theme = getTheme(card.card_name);
              return (
                <div
                  key={card.id}
                  className="group relative bg-white rounded-[2.5rem] p-4 shadow-[0_10px_40px_rgba(0,0,0,0.03)] border border-slate-100 hover:shadow-[0_30px_60px_rgba(0,0,0,0.08)] hover:-translate-y-2 transition-all duration-500"
                >
                  <div className={`rounded-[2rem] p-8 flex flex-col h-full ${theme.bg} transition-colors duration-500`}>
                    
                    <div className="flex justify-between items-start mb-10">
                      <div className={`p-4 bg-white rounded-2xl shadow-sm ${theme.accent}`}>
                        {theme.icon}
                      </div>
                      <div className="bg-white/80 backdrop-blur-md px-4 py-1.5 rounded-full text-[10px] font-black tracking-widest text-slate-500 border border-white shadow-sm">
                        {card.duration_in_months} MONTHS
                      </div>
                    </div>

                    <h3 className="text-2xl font-black text-slate-900 mb-2">{card.card_name}</h3>
                    <div className="flex items-baseline gap-1 mb-8">
                      <span className="text-4xl font-black text-slate-900 italic tracking-tighter">₹{parseFloat(card.price)}</span>
                      <span className="text-slate-400 text-sm">/yr</span>
                    </div>

                    <div className="flex-grow mb-8">
                      <div 
                        className="text-slate-600 space-y-4"
                        dangerouslySetInnerHTML={{ 
                          __html: card.benefits.replaceAll(
                            '<li>', 
                            `<li class="flex items-start gap-3 text-sm font-medium">
                              <span class="mt-0.5 text-emerald-500 shadow-sm bg-white rounded-full p-0.5">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                              </span>`
                          ) 
                        }}
                      />
                    </div>

                    <button className={`w-full py-4 rounded-2xl font-bold text-white transition-all duration-300 bg-gradient-to-r ${theme.button} hover:scale-[1.03] active:scale-95 shadow-lg shadow-slate-200`}>
                      Get Card Now
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
};

export default MyHealthCard;